/**
 * Typed client for the Strat CMS API. Every response is validated against the shared contracts,
 * every write carries an Idempotency-Key (generated if you do not pass one), and network errors,
 * 429s and 503s are retried with the SAME key, so a retry can never double an action.
 */
import {
  API_PREFIX,
  ErrorBody,
  Responses,
  exitCodeFor,
  versionHash,
  type Channel,
  type CommentBody,
  type CreateItemBody,
  type DecisionBody,
  type WithdrawApprovalBody,
  type ErrorCode,
  type ExternalRef,
  type ImportItemBody,
  type ItemStatus,
  type NewVersionBody,
  type PublicationResultBody,
  type PublishBody,
  type RequestApprovalBody,
  type ScheduleBody,
  type UpdateItemBody,
  type Version,
} from "@stratcms/contracts";
import { ServiceTokenSource } from "@stratcms/service-auth";
import type { z } from "zod";

export * from "@stratcms/contracts";

export class StratCmsError extends Error {
  constructor(
    public readonly code: ErrorCode | "NETWORK" | "BAD_RESPONSE",
    message: string,
    public readonly status: number,
    public readonly requestId: string | null,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "StratCmsError";
  }
  get exitCode(): number {
    return this.code === "BAD_RESPONSE" ? 9 : exitCodeFor(this.code);
  }
}

/** Supplies credentials per request. Bearer for machines and the CLI; cookie + CSRF for the browser. */
export interface AuthProvider {
  headers(): Promise<Record<string, string>>;
  /** Called after a 401 so a cached token is refreshed once. */
  invalidate?(): void;
}

export const noAuth: AuthProvider = { headers: async () => ({}) };

export function bearer(token: string): AuthProvider {
  return { headers: async () => ({ authorization: `Bearer ${token}` }) };
}

/** Machine identity: private_key_jwt against Strat Edge ID, five-minute tokens, refreshed as needed. */
export function serviceClient(source: ServiceTokenSource): AuthProvider {
  return {
    headers: async () => ({ authorization: `Bearer ${await source.token()}` }),
    invalidate: () => source.clear(),
  };
}

/** Browser: the session cookie travels by itself; the CSRF token is read from its cookie. */
export function browserSession(readCsrf: () => string | null): AuthProvider {
  return {
    headers: async (): Promise<Record<string, string>> => {
      const t = readCsrf();
      return t ? { "x-csrf-token": t } : {};
    },
  };
}

export interface ClientOptions {
  baseUrl: string;
  auth?: AuthProvider;
  fetch?: typeof fetch;
  retries?: number;
  userAgent?: string;
  /** Include cookies (browser use). */
  credentials?: RequestCredentials;
}

export interface CallOpts {
  idempotencyKey?: string;
  query?: Record<string, string | number | undefined | null>;
  headers?: Record<string, string>;
}

const newKey = () => `sdk-${crypto.randomUUID()}`;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class StratCmsClient {
  private readonly base: string;
  private readonly auth: AuthProvider;
  private readonly f: typeof fetch;
  private readonly retries: number;

  constructor(private readonly opts: ClientOptions) {
    this.base = opts.baseUrl.replace(/\/$/, "");
    this.auth = opts.auth ?? noAuth;
    this.f = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
    this.retries = opts.retries ?? 2;
  }

  async request<S extends z.ZodTypeAny>(method: string, path: string, schema: S, body?: unknown, o: CallOpts = {}): Promise<z.infer<S>> {
    const res = await this.raw(method, path, body, o);
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      throw new StratCmsError("BAD_RESPONSE", `Expected JSON from ${method} ${path}`, res.status, res.headers.get("x-request-id"));
    }
    const ok = schema.safeParse(parsed);
    if (!ok.success) {
      throw new StratCmsError("BAD_RESPONSE", `The server's response to ${method} ${path} did not match the contract`, res.status, res.headers.get("x-request-id"), ok.error.issues.slice(0, 5));
    }
    return ok.data;
  }

  /** Low-level call: auth, idempotency, retries and structured errors. Returns the raw Response on 2xx. */
  async raw(method: string, path: string, body?: unknown, o: CallOpts = {}): Promise<Response> {
    const url = new URL(`${this.base}${path.startsWith("/auth") ? "" : API_PREFIX}${path}`);
    for (const [k, v] of Object.entries(o.query ?? {})) if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    const unsafe = method !== "GET" && method !== "HEAD";
    const key = unsafe ? (o.idempotencyKey ?? newKey()) : undefined;
    let refreshed = false;
    for (let attempt = 0; ; attempt++) {
      const headers: Record<string, string> = { accept: "application/json", ...(await this.auth.headers()) };
      if (this.opts.userAgent) headers["user-agent"] = this.opts.userAgent;
      if (key) headers["idempotency-key"] = key;
      let payload: BodyInit | undefined;
      if (body instanceof ArrayBuffer || body instanceof Uint8Array || (typeof Blob !== "undefined" && body instanceof Blob)) {
        payload = body as BodyInit;
      } else if (body !== undefined) {
        headers["content-type"] = "application/json";
        payload = JSON.stringify(body);
      }
      let res: Response;
      try {
        res = await this.f(url.toString(), { method, headers: { ...headers, ...o.headers }, body: payload, credentials: this.opts.credentials });
      } catch (e) {
        if (attempt < this.retries) {
          await sleep(250 * 2 ** attempt);
          continue;
        }
        throw new StratCmsError("NETWORK", `Could not reach Strat CMS: ${(e as Error).message}`, 0, null);
      }
      if (res.ok) return res;
      if (res.status === 401 && !refreshed && this.auth.invalidate) {
        refreshed = true;
        this.auth.invalidate();
        continue;
      }
      if ((res.status === 429 || res.status === 503) && attempt < this.retries) {
        const ra = Number(res.headers.get("retry-after") || 0);
        await sleep(Math.min(ra ? ra * 1000 : 500 * 2 ** attempt, 10_000));
        continue;
      }
      const text = await res.text();
      let err: z.infer<typeof ErrorBody> | null = null;
      try {
        const p = ErrorBody.safeParse(JSON.parse(text));
        err = p.success ? p.data : null;
      } catch {
        err = null;
      }
      if (err) throw new StratCmsError(err.error.code, err.error.message, res.status, err.error.requestId, err.error.details);
      throw new StratCmsError(res.status >= 500 ? "INTERNAL" : "BAD_REQUEST", `HTTP ${res.status} from ${method} ${path}`, res.status, res.headers.get("x-request-id"));
    }
  }

  /* ---------------- identity */
  me() {
    return this.request("GET", "/me", Responses.me);
  }

  /* ---------------- content */
  list(q: { channel?: Channel; status?: ItemStatus; q?: string; cursor?: string; limit?: number } = {}) {
    return this.request("GET", "/content", Responses.itemList, undefined, { query: q });
  }
  get(id: string) {
    return this.request("GET", `/content/${enc(id)}`, Responses.itemDetail);
  }
  findByRef(system: string, key: string, value: string) {
    return this.request("GET", `/content/by-ref`, Responses.itemDetail, undefined, { query: { system, key, value } });
  }
  create(body: CreateItemBody, o?: CallOpts) {
    return this.request("POST", "/content", Responses.created, body, o);
  }
  newVersion(id: string, body: NewVersionBody, o?: CallOpts) {
    return this.request("POST", `/content/${enc(id)}/versions`, Responses.versioned, body, o);
  }
  update(id: string, body: UpdateItemBody, o?: CallOpts) {
    return this.request("PATCH", `/content/${enc(id)}`, Responses.itemDetail, body, o);
  }
  versions(id: string) {
    return this.request("GET", `/content/${enc(id)}/versions`, Responses.versionList);
  }
  version(id: string, versionId: string) {
    return this.request("GET", `/content/${enc(id)}/versions/${enc(versionId)}`, Responses.version);
  }
  history(id: string) {
    return this.request("GET", `/content/${enc(id)}/history`, Responses.history);
  }
  comments(id: string) {
    return this.request("GET", `/content/${enc(id)}/comments`, Responses.commentList);
  }
  comment(id: string, body: CommentBody, o?: CallOpts) {
    return this.request("POST", `/content/${enc(id)}/comments`, Responses.comment, body, o);
  }
  addRefs(id: string, refs: ExternalRef[], o?: CallOpts) {
    return this.request("POST", `/content/${enc(id)}/refs`, Responses.itemDetail, { refs }, o);
  }
  previewLink(id: string, versionId: string, ttlSeconds = 3600, o?: CallOpts) {
    return this.request("POST", `/content/${enc(id)}/preview-links`, Responses.previewLink, { versionId, ttlSeconds }, o);
  }

  /* ---------------- approvals */
  requestApproval(id: string, body: RequestApprovalBody, o?: CallOpts) {
    return this.request("POST", `/content/${enc(id)}/approval-requests`, Responses.approvalRequested, body, o);
  }
  withdrawApproval(approvalId: string, body: WithdrawApprovalBody = {}, o?: CallOpts) {
    return this.request("POST", `/approval-requests/${enc(approvalId)}/withdraw`, Responses.decided, body, o);
  }
  decide(approvalId: string, body: DecisionBody, o?: CallOpts) {
    return this.request("POST", `/approval-requests/${enc(approvalId)}/decision`, Responses.decided, body, o);
  }
  approvals(status?: string) {
    return this.request("GET", `/approval-requests`, Responses.approvalList, undefined, { query: { status } });
  }

  /* ---------------- schedule and publish */
  schedule(id: string, body: ScheduleBody, o?: CallOpts) {
    return this.request("POST", `/content/${enc(id)}/schedule`, Responses.scheduled, body, o);
  }
  cancelSchedule(scheduleId: string, o?: CallOpts) {
    return this.request("POST", `/schedules/${enc(scheduleId)}/cancel`, Responses.itemOnly, {}, o);
  }
  publish(id: string, body: PublishBody, o?: CallOpts) {
    return this.request("POST", `/content/${enc(id)}/publish`, Responses.published, body, o);
  }
  publications(q: { status?: string; channel?: Channel } = {}) {
    return this.request("GET", `/publications`, Responses.publicationList, undefined, { query: q });
  }
  claim(publicationId: string, leaseSeconds = 300, o?: CallOpts) {
    return this.request("POST", `/publications/${enc(publicationId)}/claim`, Responses.claimed, { leaseSeconds }, o);
  }
  reportResult(publicationId: string, body: PublicationResultBody, o?: CallOpts) {
    return this.request("POST", `/publications/${enc(publicationId)}/result`, Responses.published, body, o);
  }

  /* ---------------- assets */
  async upload(bytes: Uint8Array | ArrayBuffer, contentType: string, filename: string, o?: CallOpts) {
    const res = await this.raw("POST", "/assets", bytes, { ...o, headers: { "content-type": contentType, "x-filename": encodeURIComponent(filename) } });
    const parsed = Responses.uploaded.safeParse(await res.json());
    if (!parsed.success) throw new StratCmsError("BAD_RESPONSE", "Upload response did not match the contract", res.status, res.headers.get("x-request-id"));
    return parsed.data;
  }
  asset(id: string) {
    return this.request("GET", `/assets/${enc(id)}`, Responses.asset);
  }

  /* ---------------- feeds, audit, import */
  events(after = 0, limit = 100) {
    return this.request("GET", `/events`, Responses.events, undefined, { query: { after, limit } });
  }
  audit(after = 0, target?: string) {
    return this.request("GET", `/audit`, Responses.audit, undefined, { query: { after, target } });
  }
  importItem(body: ImportItemBody, o?: CallOpts) {
    return this.request("POST", `/import/items`, Responses.imported, body, o);
  }
  publishedPosts(channel: Channel = "blog") {
    return this.request("GET", `/public/posts`, Responses.snapshots, undefined, { query: { channel } });
  }
}

const enc = (s: string) => encodeURIComponent(s);

/**
 * Checks a version's hash locally before acting on it. A publisher calls this on what it claimed,
 * so it never posts bytes other than the ones a person approved.
 */
export async function verifyVersion(v: Version): Promise<boolean> {
  return (await versionHash({ itemId: v.itemId, number: v.number, channel: v.channel, payload: v.payload })) === v.hash;
}
